import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
} from '@mui/material';
import {
  PlayArrow as ClockInIcon,
  Stop as ClockOutIcon,
  Assignment as ProjectIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Error as RejectedIcon,
  TrendingUp as OvertimeIcon,
  LocationOn as LocationIcon,
  Timer as ActiveIcon,
  Assessment as AnalyticsIcon,
  Today as TodayIcon,
  DateRange as WeekIcon
} from '@mui/icons-material';

interface TimeEntry {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_number: string;
  project_id?: number;
  project_name?: string;
  task_description?: string;
  clock_in_time: string;
  clock_out_time?: string;
  break_duration_minutes: number;
  total_hours?: number;
  regular_hours?: number;
  overtime_hours?: number;
  location?: string;
  notes?: string;
  approved: boolean;
  status: 'active' | 'completed' | 'approved' | 'rejected';
}

interface Project {
  id: number;
  name: string;
  description?: string;
  client_name?: string;
  hourly_rate?: number;
  status: string;
}

const TimeTrackingDashboard: React.FC = () => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [currentDuration, setCurrentDuration] = useState('0h 0m');
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false);
  const [clockOutDialogOpen, setClockOutDialogOpen] = useState(false);
  const [selectedEmployee] = useState(1); // Mock current user
  const [clockInData, setClockInData] = useState({
    project_id: '',
    task_description: '',
    location: 'office',
    notes: ''
  });
  const [clockOutData, setClockOutData] = useState({
    break_duration_minutes: 0,
    notes: ''
  });

  useEffect(() => {
    fetchTimeEntries();
    fetchProjects();
    fetchActiveEntry();
    
    // Update current duration every minute
    const interval = setInterval(() => {
      if (activeEntry) {
        updateCurrentDuration();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [activeEntry]);

  const fetchTimeEntries = async () => {
    // Mock data for demonstration
    setTimeEntries([
      {
        id: 1,
        employee_id: 1,
        employee_name: 'John Smith',
        employee_number: 'EMP001',
        project_id: 1,
        project_name: 'Website Redesign',
        task_description: 'Frontend development',
        clock_in_time: '2024-01-15T09:00:00',
        clock_out_time: '2024-01-15T17:30:00',
        break_duration_minutes: 60,
        total_hours: 7.5,
        regular_hours: 7.5,
        overtime_hours: 0,
        location: 'office',
        notes: 'Completed user interface mockups',
        approved: true,
        status: 'approved'
      },
      {
        id: 2,
        employee_id: 1,
        employee_name: 'John Smith',
        employee_number: 'EMP001',
        project_id: 2,
        project_name: 'Internal Training',
        task_description: 'React training session',
        clock_in_time: '2024-01-16T09:15:00',
        clock_out_time: '2024-01-16T18:00:00',
        break_duration_minutes: 45,
        total_hours: 8.0,
        regular_hours: 8.0,
        overtime_hours: 0,
        location: 'office',
        notes: 'Attended React hooks workshop',
        approved: false,
        status: 'completed'
      }
    ]);
  };

  const fetchProjects = async () => {
    setProjects([
      {
        id: 1,
        name: 'Website Redesign',
        description: 'Complete website overhaul for client',
        client_name: 'ABC Corp',
        hourly_rate: 75.00,
        status: 'active'
      },
      {
        id: 2,
        name: 'Internal Training',
        description: 'Staff development and training',
        client_name: 'Internal',
        hourly_rate: 0.00,
        status: 'active'
      }
    ]);
  };

  const fetchActiveEntry = async () => {
    // Mock check for active entry
    setActiveEntry(null);
    setCurrentDuration('0h 0m');
  };

  const updateCurrentDuration = () => {
    if (activeEntry) {
      const start = new Date(activeEntry.clock_in_time);
      const now = new Date();
      const diff = now.getTime() - start.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setCurrentDuration(`${hours}h ${minutes}m`);
    }
  };

  const handleClockIn = async () => {
    const clockInTime = new Date().toISOString();
    
    const newEntry: TimeEntry = {
      id: Date.now(),
      employee_id: selectedEmployee,
      employee_name: 'John Smith',
      employee_number: 'EMP001',
      project_id: clockInData.project_id ? parseInt(clockInData.project_id) : undefined,
      project_name: projects.find(p => p.id === parseInt(clockInData.project_id))?.name,
      task_description: clockInData.task_description,
      clock_in_time: clockInTime,
      break_duration_minutes: 0,
      location: clockInData.location,
      notes: clockInData.notes,
      approved: false,
      status: 'active'
    };

    setActiveEntry(newEntry);
    setTimeEntries(prev => [newEntry, ...prev]);
    setClockInDialogOpen(false);
    
    // Reset form
    setClockInData({
      project_id: '',
      task_description: '',
      location: 'office',
      notes: ''
    });
  };

  const handleClockOut = async () => {
    if (!activeEntry) return;

    const clockOutTime = new Date().toISOString();
    const start = new Date(activeEntry.clock_in_time);
    const end = new Date(clockOutTime);
    const totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const workedMinutes = totalMinutes - clockOutData.break_duration_minutes;
    const totalHours = workedMinutes / 60;
    const regularHours = Math.min(totalHours, 8);
    const overtimeHours = Math.max(0, totalHours - 8);

    const updatedEntry: TimeEntry = {
      ...activeEntry,
      clock_out_time: clockOutTime,
      break_duration_minutes: clockOutData.break_duration_minutes,
      total_hours: Math.round(totalHours * 100) / 100,
      regular_hours: Math.round(regularHours * 100) / 100,
      overtime_hours: Math.round(overtimeHours * 100) / 100,
      notes: clockOutData.notes || activeEntry.notes,
      status: 'completed'
    };

    setTimeEntries(prev => prev.map(entry => 
      entry.id === activeEntry.id ? updatedEntry : entry
    ));
    
    setActiveEntry(null);
    setCurrentDuration('0h 0m');
    setClockOutDialogOpen(false);
    
    // Reset form
    setClockOutData({
      break_duration_minutes: 0,
      notes: ''
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timeString: string) => {
    return new Date(timeString).toLocaleDateString('en-GB');
  };

  const formatHours = (hours?: number) => {
    if (!hours) return '0.0h';
    return `${hours.toFixed(1)}h`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'completed': return 'warning';
      case 'active': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <ApprovedIcon />;
      case 'completed': return <PendingIcon />;
      case 'active': return <ActiveIcon />;
      case 'rejected': return <RejectedIcon />;
      default: return <PendingIcon />;
    }
  };

  const totalHoursToday = timeEntries
    .filter(entry => entry.clock_in_time.startsWith(new Date().toISOString().split('T')[0]))
    .reduce((sum, entry) => sum + (entry.total_hours || 0), 0);

  const totalOvertimeThisWeek = timeEntries
    .reduce((sum, entry) => sum + (entry.overtime_hours || 0), 0);

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Time Tracking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track time, manage projects, and streamline payroll integration
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeEntry ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<ClockOutIcon />}
              onClick={() => setClockOutDialogOpen(true)}
              size="large"
            >
              Clock Out
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<ClockInIcon />}
              onClick={() => setClockInDialogOpen(true)}
              size="large"
            >
              Clock In
            </Button>
          )}
        </Box>
      </Box>

      {/* Active Time Entry Alert */}
      {activeEntry && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                ⏰ Currently Clocked In
              </Typography>
              <Typography variant="body2">
                Started at {formatTime(activeEntry.clock_in_time)} • 
                {activeEntry.project_name && ` Working on: ${activeEntry.project_name}`}
                {activeEntry.task_description && ` - ${activeEntry.task_description}`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                {currentDuration}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Current Duration
              </Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TodayIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {formatHours(totalHoursToday)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hours Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WeekIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {formatHours(timeEntries.reduce((sum, entry) => sum + (entry.total_hours || 0), 0))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hours This Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <OvertimeIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {formatHours(totalOvertimeThisWeek)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overtime This Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ProjectIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {projects.filter(p => p.status === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Projects
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Time Entries */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Recent Time Entries</Typography>
            <Button startIcon={<AnalyticsIcon />} variant="outlined" size="small">
              View Analytics
            </Button>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Overtime</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(entry.clock_in_time)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {entry.project_name || 'No Project'}
                        </Typography>
                        {entry.task_description && (
                          <Typography variant="caption" color="text.secondary">
                            {entry.task_description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTime(entry.clock_in_time)} - {entry.clock_out_time ? formatTime(entry.clock_out_time) : 'Active'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {formatHours(entry.total_hours)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={entry.overtime_hours && entry.overtime_hours > 0 ? 'warning.main' : 'text.secondary'}>
                        {formatHours(entry.overtime_hours)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<LocationIcon />}
                        label={(entry.location ? entry.location.charAt(0).toUpperCase() + entry.location.slice(1) : 'Office')}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        color={getStatusColor(entry.status) as any}
                        size="small"
                        icon={getStatusIcon(entry.status)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Clock In Dialog */}
      <Dialog open={clockInDialogOpen} onClose={() => setClockInDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClockInIcon color="primary" />
            Clock In
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Project (Optional)</InputLabel>
                <Select
                  value={clockInData.project_id}
                  onChange={(e) => setClockInData(prev => ({ ...prev, project_id: e.target.value }))}
                >
                  <MenuItem value="">No Project</MenuItem>
                  {projects.filter(p => p.status === 'active').map(project => (
                    <MenuItem key={project.id} value={project.id.toString()}>
                      {project.name} - {project.client_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Description"
                value={clockInData.task_description}
                onChange={(e) => setClockInData(prev => ({ ...prev, task_description: e.target.value }))}
                placeholder="What are you working on?"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={clockInData.location}
                  onChange={(e) => setClockInData(prev => ({ ...prev, location: e.target.value }))}
                >
                  <MenuItem value="office">Office</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                  <MenuItem value="client_site">Client Site</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="field">Field Work</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={2}
                value={clockInData.notes}
                onChange={(e) => setClockInData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockInDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleClockIn}>
            Clock In
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clock Out Dialog */}
      <Dialog open={clockOutDialogOpen} onClose={() => setClockOutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClockOutIcon color="error" />
            Clock Out
          </Box>
        </DialogTitle>
        <DialogContent>
          {activeEntry && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Current Session</Typography>
              <Typography variant="body2">
                Started: {formatTime(activeEntry.clock_in_time)} • Duration: {currentDuration}
              </Typography>
              {activeEntry.project_name && (
                <Typography variant="body2">
                  Project: {activeEntry.project_name}
                </Typography>
              )}
            </Box>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Break Duration (minutes)"
                type="number"
                value={clockOutData.break_duration_minutes}
                onChange={(e) => setClockOutData(prev => ({ ...prev, break_duration_minutes: parseInt(e.target.value) || 0 }))}
                helperText="Total break time taken during this session"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Session Notes (Optional)"
                multiline
                rows={3}
                value={clockOutData.notes}
                onChange={(e) => setClockOutData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="What did you accomplish in this session?"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockOutDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClockOut}>
            Clock Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingDashboard;
